/*
    What a plan costs once a discount code is applied — static#74, Dave's
    first acceptance criterion: "Update the amount on the Summary".

    The checkout pages pass their price as a display string ("$9", "$399"),
    so that is what there is to work from. Everything here is DISPLAY ONLY:
    Stripe calculates the real amount from the discount attached to the
    subscription, and the backend resolves the code again at subscribe time.
    Nothing the browser computes is ever sent or trusted.
*/

/** The number inside "$399", or null if there isn't one. */
export const parsePrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : null;
  if (typeof price !== "string") return null;
  const match = price.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

/** "$9" -> "$9.00"; keeps whatever currency symbol the page used. */
const format = (amount, original) => {
  const symbol =
    typeof original === "string" && /^[^\d\s-]/.test(original.trim())
      ? original.trim()[0]
      : "$";
  return `${symbol}${amount.toFixed(2)}`;
};

/**
 * The price after a discount, as a string to render.
 *
 * Returns null when there is nothing to show — no discount, or a price this
 * cannot read — and the caller then renders the original untouched, which is
 * the honest fallback for a display-only calculation.
 *
 * @param {string|number} price    the page's advertised price
 * @param {?object} discount       from GET /payment/coupon
 * @returns {?string}
 */
export const discountedPrice = (price, discount) => {
  if (!discount) return null;
  const base = parsePrice(price);
  if (base === null) return null;

  let next = base;
  if (typeof discount.percentOff === "number") {
    next = base * (1 - discount.percentOff / 100);
  } else if (typeof discount.amountOff === "number") {
    // Stripe reports amount_off in the currency's minor unit.
    next = base - discount.amountOff / 100;
  } else {
    return null;
  }

  if (!Number.isFinite(next)) return null;
  return format(Math.max(next, 0), price);
};

/**
 * How long the discount lasts, in a few words, so the summary does not imply
 * a permanent price when the code only covers the first month.
 */
export const discountDuration = (discount) => {
  if (!discount) return null;
  if (discount.duration === "forever") return "on every payment";
  if (discount.duration === "once") return "on your first payment";
  if (discount.duration === "repeating" && discount.durationInMonths) {
    const months = discount.durationInMonths;
    return `for your first ${months} month${months === 1 ? "" : "s"}`;
  }
  return null;
};
